import React, { ReactNode } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import Tab from 'Common/Components/Tab/Tab';
import { Nav } from 'Common/Components/Tab/Nav';
import { useTabContext } from 'Common/Components/Tab/TabContext';
import { FileText, Info, SlidersHorizontal } from 'lucide-react';
import GeneralSection from './GeneralSection';
import AboutUsSection from './AboutUsSection';
import TermsSection from './TermsSection';

const GENERAL_TAB = 'general';
const ABOUT_TAB = 'about';
const TERMS_TAB = 'terms';

interface SettingsTabProps {
  eventKey: string;
  icon: ReactNode;
  children: ReactNode;
  /** Supplied by Nav.Item, which clones its child to attach the tab switch. */
  onClick?: () => void;
}

/**
 * The template ships tab primitives but no tab styling, so the active state is
 * read from the tab context and styled here.
 */
const SettingsTab = ({ eventKey, icon, children, onClick }: SettingsTabProps) => {
  const { activeTab } = useTabContext();
  const isActive = activeTab === eventKey;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-selected={isActive}
      role="tab"
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
        isActive
          ? 'border-custom-500 text-custom-500'
          : 'border-transparent text-slate-500 dark:text-zink-300 hover:text-slate-800 dark:hover:text-zink-100'
      }`}
    >
      {icon}
      {children}
    </button>
  );
};

const Settings = () => {
  document.title = 'Settings | LyfeAdmin';

  return (
    <React.Fragment>
      <BreadCrumb title="Settings" pageTitle="Dashboard" />

      <div className="col-span-12 card">
        {/* Panes stay mounted and are hidden with CSS, so switching tabs does
            not throw away an in-progress edit in either editor. */}
        <Tab.Container defaultActiveKey={GENERAL_TAB}>
          <Nav
            className="flex flex-wrap px-4 border-b border-slate-200 dark:border-zink-500"
            as="div"
          >
            <Nav.Item eventKey={GENERAL_TAB} className="" as="div">
              <SettingsTab
                eventKey={GENERAL_TAB}
                icon={<SlidersHorizontal className="size-4" />}
              >
                General
              </SettingsTab>
            </Nav.Item>

            <Nav.Item eventKey={ABOUT_TAB} className="" as="div">
              <SettingsTab eventKey={ABOUT_TAB} icon={<Info className="size-4" />}>
                About Us
              </SettingsTab>
            </Nav.Item>

            <Nav.Item eventKey={TERMS_TAB} className="" as="div">
              <SettingsTab
                eventKey={TERMS_TAB}
                icon={<FileText className="size-4" />}
              >
                Terms &amp; Conditions
              </SettingsTab>
            </Nav.Item>
          </Nav>

          <Tab.Content className="card-body">
            <Tab.Pane eventKey={GENERAL_TAB}>
              <GeneralSection />
            </Tab.Pane>

            <Tab.Pane eventKey={ABOUT_TAB}>
              <AboutUsSection />
            </Tab.Pane>

            <Tab.Pane eventKey={TERMS_TAB}>
              <TermsSection />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    </React.Fragment>
  );
};

export default Settings;
